import { useSelector } from '@tanstack/react-form';
import { Card, Heading, Icon, Text } from '@ui/themed';
import { t } from 'i18next';
import { useEffect, useMemo } from 'react';
import type { IconName } from '../../../../components/ui/icon/types';
import { useHandlers } from '../../../../context/page-context';
import { useSettings } from '../../../../context/settings-context';
import { useStockLocations } from '../../../../hooks/use-stock-locations';
import { useAllPriceListsWithManualPriceCounts } from '../../../price-lists';
import {
  CUSTOMER_GROUP_BASE_SLUG,
  SETTING_KEY_TO_LABEL,
  SettingsDefaultsMap,
} from '../../constants';
import { useCustomerGroupBuilderForm } from '../../hooks/use-customer-group-builder-form';
import { useCustomerGroups } from '../../hooks/use-customer-groups';
import {
  buildHierarchialGroup,
  filterSettings,
  getFormattedParentSettings,
} from '../../transformers';
import type { CustomerGroup, FormattedSettingItem } from '../../types/customer-group-response';
import type { SettingComponentMapKeys, SettingKey } from '../../types/settings.types';

const SETTING_KEY_TO_ICON: Record<SettingKey, IconName> = {
  priceListSelection: 'priceList',
  allowedPaymentMethods: 'payment',
  quotingEngine: 'calculator',
  orderItemQuantityValidation: 'cube',
  orderTotalsValidation: 'inbox',
  allowAddressEditing: 'document',
  allowDefaultBillingModification: 'document',
  paymentOnAccountEnforceCreditLimit: 'lock',
  multiLocationStockConfig: 'mapPin',
  useMultiLocationStock: 'mapPin',
  stockDisplay: 'checkCircle',
  allowCheckout: 'alertTriangle',
};

function SettingSummary({ setting }: { setting?: FormattedSettingItem }) {
  if (!setting?.value) {
    return (
      <Text variant="primary" size="md" truncate>
        {t('labels:none')}
      </Text>
    );
  }

  return (
    <Text variant="primary" size="md" truncate>
      {setting.value}
    </Text>
  );
}

// combine parent customer group's formattedSettings with its formattedParentSettings
function getParentCustomerGroupFormattedSettings(group?: CustomerGroup) {
  return getFormattedParentSettings(group?.formattedSettings, group?.formattedParentSettings);
}

export function OverrideSettingsCard({
  scrollToSection,
  isEditing,
}: {
  scrollToSection: (settingkey: SettingComponentMapKeys) => void;
  isEditing?: boolean;
}) {
  const { notificationHandler } = useHandlers();
  const form = useCustomerGroupBuilderForm();
  const featureSettings = useSettings();
  const isQuotingEngineEnabled = featureSettings.quotingEnabled;
  const isMultiStockEnabled = featureSettings.useMultiLocationStock;

  const { data: customerGroups, isError: isCustomerGroupError } = useCustomerGroups();
  const { data: priceLists, isError: isPriceListsError } = useAllPriceListsWithManualPriceCounts();
  const { stockLocations, isError: isStockLocationsError } = useStockLocations();

  useEffect(() => {
    if (isCustomerGroupError || isPriceListsError || isStockLocationsError) {
      const failedItems = [
        isCustomerGroupError &&
          t(
            'features:customer_group.components.override_settings_card.notification.errors.customer_groups',
          ),
        isPriceListsError &&
          t(
            'features:customer_group.components.override_settings_card.notification.errors.price_lists',
          ),
        isStockLocationsError &&
          t(
            'features:customer_group.components.override_settings_card.notification.errors.stock_locations',
          ),
      ]
        .filter(Boolean)
        .join(', ');

      if (!failedItems) {
        return;
      }

      notificationHandler.error(
        t('features:customer_group.components.override_settings_card.notification.errors.fetch', {
          failedItems: failedItems,
        }),
      );
    }
  }, [isCustomerGroupError, isPriceListsError, isStockLocationsError]);

  const settings = useSelector(form.store, (state) => state.values.settings);
  const slug = useSelector(form.store, (state) => (isEditing ? state.values.slug : ''));
  const parent_slug = useSelector(form.store, (state) => state.values.parent_slug);

  const hierarchicalGroups = useMemo(
    () =>
      buildHierarchialGroup(
        customerGroups,
        priceLists,
        stockLocations,
        isQuotingEngineEnabled,
        isMultiStockEnabled,
      ),
    [customerGroups, priceLists, stockLocations, isQuotingEngineEnabled, isMultiStockEnabled],
  );

  // if editing base customer group, show its formattedParentSettings, other get the parent customer
  // group formatted settings to display as inheritedSettings
  const settingsWithInheritedProperties =
    isEditing && slug === CUSTOMER_GROUP_BASE_SLUG
      ? hierarchicalGroups.find((group) => group.slug === slug)?.formattedParentSettings
      : getParentCustomerGroupFormattedSettings(
          hierarchicalGroups.find((group) => group.slug === parent_slug),
        );

  const isSomeSettingsUndefined = useMemo(
    () =>
      filterSettings(settings).some((setting) => {
        return setting.value === undefined;
      }),
    [settings],
  );

  const settingsToOverride = useMemo(
    () => filterSettings(settings).filter((setting) => setting.value === undefined),
    [settings],
  );

  // hide this card if all settings are overridden
  if (!isSomeSettingsUndefined) {
    return null;
  }

  return (
    <Card hideBackground>
      <Card.Header headingLevel="h2">
        {t('features:customer_group.components.override_settings_card.title')}
      </Card.Header>
      <Card.Body>
        <div className="mt-2 flex flex-col gap-1 whitespace-nowrap">
          {settingsToOverride.map((setting) => (
            <button
              key={setting.key}
              type="button"
              className="grid cursor-pointer grid-cols-[1fr_auto] items-center rounded-lg px-2 py-2 transition hover:bg-gray-200 sm:grid-cols-[12rem_1fr_auto]"
              onClick={() => {
                scrollToSection(setting.key);
                form.setFieldValue(`settings.${setting.key}`, SettingsDefaultsMap[setting.key]);
              }}
            >
              <span className="flex items-center gap-1">
                <Icon name={SETTING_KEY_TO_ICON[setting.key]} size="sm" />
                <Heading level="h3" style="sm" weight="semibold">
                  {SETTING_KEY_TO_LABEL[setting.key]}
                </Heading>
              </span>
              <span className="hidden items-center gap-2 truncate sm:flex">
                <span className="group flex gap-1 opacity-40">
                  <Icon name="user" />
                  <Icon name="arrowRight" />
                </span>
                <SettingSummary
                  setting={settingsWithInheritedProperties?.find(
                    (inheritedSetting) => inheritedSetting.key === setting.key,
                  )}
                />
              </span>
              <span className="pointer-events-none">
                <Icon name="add" size="sm" />
              </span>
            </button>
          ))}
        </div>
      </Card.Body>
    </Card>
  );
}
